import { setHeaderCartCount } from '../utils/cart-count.js';

class MiniCart {
  constructor() {
    this.root = document.querySelector('[js-ird-minicart]');
    this.isUpdating = false;

    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDocumentSubmit = this.onDocumentSubmit.bind(this);
    this.onRootClick = this.onRootClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.attachDocumentEvents();
    this.attachRootEvents();
  }

  attachDocumentEvents() {
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('submit', this.onDocumentSubmit);
    document.addEventListener('keydown', this.onKeydown);
  }

  attachRootEvents() {
    if (!this.root) return;
    this.root.addEventListener('click', this.onRootClick);
  }

  detachRootEvents() {
    if (!this.root) return;
    this.root.removeEventListener('click', this.onRootClick);
  }

  onDocumentClick(event) {
    const toggle = event.target.closest('[js-cart-toggle]');
    if (toggle) {
      event.preventDefault();
      this.open();
      return;
    }

    const cardAtcButton = event.target.closest('[js-ird-add-to-cart-button]');
    if (cardAtcButton) {
      event.stopPropagation();
    }
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;
    if (document.body.classList.contains('-quote-drawer-open')) return;
    this.close();
  }

  onRootClick(event) {
    const closeTrigger = event.target.closest('[js-minicart-close]');
    if (closeTrigger) {
      event.preventDefault();
      this.close();
      return;
    }

    const qtyButton = event.target.closest('[js-ird-minicart-qty]');
    if (qtyButton) {
      event.preventDefault();
      this.handleQty(qtyButton);
      return;
    }

    const removeButton = event.target.closest('[js-ird-minicart-remove]');
    if (removeButton) {
      event.preventDefault();
      this.handleRemove(removeButton);
    }
  }

  onDocumentSubmit(event) {
    const form = event.target.closest('[js-ird-add-to-cart-form]');
    if (!form) return;

    event.preventDefault();
    event.stopPropagation();
    this.addToCart(form);
  }

  open({ replay = false } = {}) {
    const body = document.body;
    const isAlreadyOpen = body.classList.contains('-minicart-open');

    if (isAlreadyOpen && !replay) {
      this.syncToggleState(true);
      return;
    }

    if (isAlreadyOpen && replay) {
      body.classList.remove('-minicart-open');
      this.syncToggleState(false);
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        body.classList.remove('-menu-open', '-desktop-menu-open', '-quote-drawer-open', '-search-open');
        body.classList.add('-minicart-open');
        this.syncToggleState(true);
        document.querySelectorAll('[js-header-menu-toggle]').forEach((toggle) => {
          toggle.setAttribute('aria-expanded', 'false');
        });
        document.querySelectorAll('[js-search-toggle]').forEach((toggle) => {
          toggle.setAttribute('aria-expanded', 'false');
        });
        this.toggleLenis(false);
      });
    });
  }

  close() {
    document.body.classList.remove('-minicart-open');
    this.syncToggleState(false);
    this.toggleLenis(true);
  }

  toggleLenis(shouldStart) {
    if (!window.lenis) return;
    if (shouldStart) {
      window.lenis.start();
    } else {
      window.lenis.stop();
    }
  }

  syncToggleState(expanded) {
    document.querySelectorAll('[js-cart-toggle]').forEach((toggle) => {
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  setBodyLoading(isLoading) {
    document.body.classList.toggle('-cart-loading', Boolean(isLoading));
  }

  async addToCart(form) {
    if (this.isUpdating) return;

    const submitButton = form.querySelector('[type="submit"]');

    this.isUpdating = true;
    this.setBodyLoading(true);
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.classList.add('-loading');
    }
    if (this.root) this.root.classList.add('-loading');

    try {
      const formData = new FormData(form);
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        body: formData,
      });

      if (!response.ok) throw new Error('Add to cart failed');

      const cart = await this.fetchCart();
      setHeaderCartCount(cart?.item_count || 0);
      this.dispatchCartUpdated(cart);
      await this.refresh();
      this.open({ replay: true });
    } catch (error) {
      console.error('[IRD MiniCart] add to cart error:', error);
    } finally {
      this.isUpdating = false;
      this.setBodyLoading(false);
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.classList.remove('-loading');
      }
      if (this.root) this.root.classList.remove('-loading');
    }
  }

  handleQty(button) {
    if (this.isUpdating) return;

    const item = button.closest('.ird-minicart__item');
    const input = item?.querySelector('.ird-minicart__qty input');
    const line = Number(item?.dataset.line);
    const delta = Number(button.dataset.delta || 0);
    const currentQty = Number(input?.value || 0);
    const nextQty = Math.max(0, currentQty + delta);

    if (!line || nextQty === currentQty) return;
    this.updateLine(line, nextQty);
  }

  handleRemove(button) {
    if (this.isUpdating) return;

    const item = button.closest('.ird-minicart__item');
    const line = Number(item?.dataset.line);
    if (!line) return;

    this.updateLine(line, 0);
  }

  async updateLine(line, quantity) {
    this.isUpdating = true;
    if (this.root) this.root.classList.add('-loading');

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ line, quantity }),
      });

      if (!response.ok) throw new Error('Cart update failed');

      const cart = await response.json();
      setHeaderCartCount(cart?.item_count || 0);
      this.dispatchCartUpdated(cart);
      await this.refresh();

      if ((cart?.item_count || 0) === 0) {
        this.open();
      }
    } catch (error) {
      console.error('[IRD MiniCart] update error:', error);
    } finally {
      this.isUpdating = false;
      if (this.root) this.root.classList.remove('-loading');
    }
  }

  async fetchCart() {
    const response = await fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) throw new Error('Unable to fetch cart');
    return response.json();
  }

  dispatchCartUpdated(cart) {
    document.dispatchEvent(
      new CustomEvent('ird:cart:updated', {
        detail: {
          itemCount: cart?.item_count || 0,
          cart,
        },
      })
    );
  }

  async refresh() {
    if (!this.root) {
      this.root = document.querySelector('[js-ird-minicart]');
      if (!this.root) return;
    }

    const sectionId = this.root.dataset.sectionId;
    if (!sectionId) return;

    const response = await fetch(`/?section_id=${encodeURIComponent(sectionId)}`, {
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('Mini cart refresh failed');

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const nextRoot = doc.querySelector('[js-ird-minicart]');

    if (!nextRoot || !this.root) return;

    this.detachRootEvents();
    this.root.replaceWith(nextRoot);
    this.root = nextRoot;
    this.attachRootEvents();
  }
}

export default MiniCart;
